import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Card, Button, Input, Select } from '../../styles/components';
import { Organization } from '../../store/api/organizationsApi';
import { useGetBusinessSystemsQuery } from '../../store/api/businessSystemsApi';
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

const FormContainer = styled(Card)`
  padding: ${props => props.theme.spacing.xl};
  max-width: 32rem;
  width: 100%;
`;

const FormHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const FormTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes.xl};
  font-weight: ${props => props.theme.fontWeights.semibold};
  color: ${props => props.theme.colors.gray[900]};
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: none;
  background: none;
  color: ${props => props.theme.colors.gray[400]};
  border-radius: ${props => props.theme.borders.radius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    color: ${props => props.theme.colors.gray[600]};
    background-color: ${props => props.theme.colors.gray[100]};
  }
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const FormGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const FormLabel = styled.label`
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  color: ${props => props.theme.colors.gray[700]};
`;

const RequiredIndicator = styled.span`
  color: ${props => props.theme.colors.error[500]};
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.error[600]};
  
  svg {
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.xl};
`;

const CancelButton = styled(Button)`
  background-color: ${props => props.theme.colors.gray[100]};
  color: ${props => props.theme.colors.gray[700]};
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.gray[200]};
  }
`;

const SubmitButton = styled(Button)`
  flex: 1;
`;

const CheckboxField = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.gray[50]};
  border-radius: ${props => props.theme.borders.radius.md};
  border: 1px solid ${props => props.theme.colors.gray[200]};
`;

const CheckboxInput = styled.input`
  width: 1rem;
  height: 1rem;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const CheckboxLabel = styled.label<{ $disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
  flex: 1;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$disabled ? 0.5 : 1};
`;

const CheckboxLabelText = styled.span`
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  color: ${props => props.theme.colors.gray[700]};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};

  svg {
    width: 1rem;
    height: 1rem;
    color: ${props => props.theme.colors.primary[600]};
  }
`;

const CheckboxHelperText = styled.span`
  font-size: ${props => props.theme.fontSizes.xs};
  color: ${props => props.theme.colors.gray[500]};
`;

interface OrganizationFormProps {
  organization?: Organization;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: OrganizationFormData) => void;
  isLoading?: boolean;
  error?: string;
}

export interface OrganizationFormData {
  organizationId: string;
  name: string;
  businessSystemId?: string;
  useSystemCertificate?: boolean;
}

export function OrganizationForm({ 
  organization, 
  isOpen, 
  onClose, 
  onSubmit, 
  isLoading = false,
  error 
}: OrganizationFormProps) {
  const [formData, setFormData] = useState<OrganizationFormData>({
    organizationId: '',
    name: '',
    businessSystemId: '',
    useSystemCertificate: false
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const { data: businessSystems } = useGetBusinessSystemsQuery();

  useEffect(() => {
    if (organization) {
      setFormData({
        organizationId: organization.organizationId,
        name: organization.name,
        businessSystemId: organization.businessSystemId || '',
        useSystemCertificate: organization.useSystemCertificate || false
      });
    } else {
      setFormData({
        organizationId: '',
        name: '',
        businessSystemId: '',
        useSystemCertificate: false
      });
    }
    setValidationErrors({});
  }, [organization, isOpen]);

  const handleInputChange = (field: keyof OrganizationFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.organizationId.trim()) {
      errors.organizationId = 'Organization ID is required';
    }

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      onSubmit({
        ...formData,
        businessSystemId: formData.businessSystemId || undefined,
        useSystemCertificate: formData.useSystemCertificate
      });
    }
  };

  const handleClose = () => {
    setFormData({
      organizationId: '',
      name: '',
      businessSystemId: '',
      useSystemCertificate: false
    });
    setValidationErrors({});
    onClose();
  };

  const hasBusinessSystem = Boolean(formData.businessSystemId);
  const selectedBusinessSystem = businessSystems?.find(bs => bs.id === formData.businessSystemId);

  if (!isOpen) return null;

  return (
    <FormContainer>
      <FormHeader>
        <FormTitle>
          {organization ? 'Edit Organization' : 'Add Organization'}
        </FormTitle>
        <CloseButton onClick={handleClose} type="button">
          <XMarkIcon />
        </CloseButton>
      </FormHeader>

      <form onSubmit={handleSubmit}>
        <FormGrid>
          <FormField>
            <FormLabel>
              Organization ID <RequiredIndicator>*</RequiredIndicator>
            </FormLabel>
            <Input
              value={formData.organizationId}
              onChange={(e) => handleInputChange('organizationId', e.target.value)}
              placeholder="Enter organization ID"
              disabled={isLoading}
            />
            {validationErrors.organizationId && (
              <ErrorMessage>
                <ExclamationTriangleIcon />
                {validationErrors.organizationId}
              </ErrorMessage>
            )}
          </FormField>

          <FormField>
            <FormLabel>
              Name <RequiredIndicator>*</RequiredIndicator>
            </FormLabel>
            <Input
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Enter organization name"
              disabled={isLoading}
            />
            {validationErrors.name && (
              <ErrorMessage>
                <ExclamationTriangleIcon />
                {validationErrors.name}
              </ErrorMessage>
            )}
          </FormField>

          <FormField>
            <FormLabel>Business System</FormLabel>
            <Select
              value={formData.businessSystemId}
              onChange={(e) => handleInputChange('businessSystemId', e.target.value)}
              disabled={isLoading}
            >
              <option value="">Select a business system (optional)</option>
              {(businessSystems || []).map((bs) => (
                <option key={bs.id} value={bs.id}>
                  {bs.name}
                </option>
              ))}
            </Select>
          </FormField>

          {organization && (
            <CheckboxField>
              <CheckboxInput
                type="checkbox"
                id="useSystemCertificate"
                checked={formData.useSystemCertificate || false}
                disabled={!hasBusinessSystem || isLoading}
                onChange={(e) => handleInputChange('useSystemCertificate', e.target.checked)}
              />
              <CheckboxLabel htmlFor="useSystemCertificate" $disabled={!hasBusinessSystem}>
                <CheckboxLabelText>
                  <ShieldCheckIcon />
                  Use System Certificate
                </CheckboxLabelText>
                <CheckboxHelperText>
                  {hasBusinessSystem && selectedBusinessSystem
                    ? `Use the shared certificate for ${selectedBusinessSystem.name}`
                    : 'Select a business system to enable this option'}
                </CheckboxHelperText>
              </CheckboxLabel>
            </CheckboxField>
          )}

          {error && (
            <ErrorMessage>
              <ExclamationTriangleIcon />
              {error}
            </ErrorMessage>
          )}
        </FormGrid>

        <FormActions>
          <CancelButton type="button" onClick={handleClose} disabled={isLoading}>
            Cancel
          </CancelButton>
          <SubmitButton type="submit" disabled={isLoading}>
            {isLoading ? 'Saving...' : organization ? 'Update' : 'Create'}
          </SubmitButton>
        </FormActions>
      </form>
    </FormContainer>
  );
}